/**
 * GSAP Viewer Configuration
 * Central configuration for GSAP slide viewer integration
 */

export const DEFAULT_SLIDE_RESOLUTION = {
  width: 1920,
  height: 1080,
} as const;

/**
 * Cache version for gsap-framework static files.
 * Bump this when gsap-framework files change to bust browser cache.
 *
 * The value flows: parent React → iframe URL `?v=` → inline loader in
 * video-composition.html stamps it onto every framework <script src>.
 * So bumping here transitively busts all gsap-framework JS on next load.
 */
export const GSAP_CACHE_VERSION = '27';

export const GSAP_VIEWER_CONFIG = {
  /**
   * Path to the GSAP viewer HTML file
   */
  viewerPath: '/gsap_framework/single-slide.html',

  /**
   * Default slide resolution (16:9)
   */
  slideResolution: DEFAULT_SLIDE_RESOLUTION,

  /**
   * Default aspect ratio for slides (16:9)
   */
  aspectRatio: 16 / 9,

  /**
   * URL parameters for gallery view (no edit, no controls)
   */
  galleryParams: {
    editMode: false,
    controls: false,
  },

  /**
   * URL parameters for preview view (full features)
   */
  previewParams: {
    editMode: true,
    controls: true,
  },

  /**
   * URL parameters for public view (full controls, no edit)
   * Used for publicly shared slides via share links
   */
  publicParams: {
    editMode: false,
    controls: true,
    uiMode: 'public' as const,
  },

  /**
   * Build viewer URL with parameters
   */
  buildUrl(
    slidePath: string,
    options: {
      editMode?: boolean;
      controls?: boolean;
      conversationId?: string;
      uiMode?: 'embedded' | 'external' | 'public';
      resolution?: { width: number; height: number };
    } = {}
  ): string {
    const params = new URLSearchParams();
    params.set('v', GSAP_CACHE_VERSION);
    params.set('slide', slidePath);

    if (options.editMode !== undefined) {
      params.set('editMode', options.editMode.toString());
    }

    if (options.controls !== undefined) {
      params.set('controls', options.controls.toString());
    }

    if (options.conversationId) {
      params.set('conversationId', options.conversationId);
    }

    if (options.uiMode) {
      params.set('uiMode', options.uiMode);
    }

    if (options.resolution?.width && options.resolution?.height) {
      params.set('width', String(options.resolution.width));
      params.set('height', String(options.resolution.height));
    }

    return `${this.viewerPath}?${params.toString()}`;
  },

  /**
   * Get gallery view URL
   */
  getGalleryUrl(slidePath: string, conversationId?: string): string {
    return this.buildUrl(slidePath, {
      ...this.galleryParams,
      conversationId,
    });
  },

  /**
   * Get preview view URL
   */
  getPreviewUrl(slidePath: string, conversationId?: string): string {
    return this.buildUrl(slidePath, {
      ...this.previewParams,
      conversationId,
    });
  },

  /**
   * Get public view URL for shared slides
   * Full playback controls but no edit features
   */
  getPublicUrl(slidePath: string): string {
    return this.buildUrl(slidePath, this.publicParams);
  },
};

/**
 * Parse a resolution string like "1080x1080" into { width, height }.
 * Returns DEFAULT_SLIDE_RESOLUTION if the string is invalid or absent.
 */
export function parseResolutionString(
  resolutionStr?: string | null
): { width: number; height: number } {
  if (!resolutionStr) return { ...DEFAULT_SLIDE_RESOLUTION };
  const match = resolutionStr.match(/^(\d+)x(\d+)$/);
  if (!match) return { ...DEFAULT_SLIDE_RESOLUTION };
  return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) };
}

/**
 * Calculate scale to fit container while maintaining aspect ratio.
 * Accepts optional resolution override (defaults to 1920x1080).
 */
export function calculateScale(
  containerWidth: number,
  containerHeight: number,
  resolution: { width: number; height: number } = DEFAULT_SLIDE_RESOLUTION
): number {
  const scaleX = containerWidth / resolution.width;
  const scaleY = containerHeight / resolution.height;
  return Math.min(scaleX, scaleY);
}

/**
 * Calculate centered position for scaled slide.
 * Accepts optional resolution override (defaults to 1920x1080).
 */
export function calculateCenterOffset(
  containerWidth: number,
  containerHeight: number,
  scale: number,
  resolution: { width: number; height: number } = DEFAULT_SLIDE_RESOLUTION
): { x: number; y: number } {
  const scaledWidth = resolution.width * scale;
  const scaledHeight = resolution.height * scale;

  return {
    x: (containerWidth - scaledWidth) / 2,
    y: (containerHeight - scaledHeight) / 2,
  };
}
